/**
 * Enrollment. Landing shows current voiceprints + entry to wizard.
 *
 * Wizard flow (auto-pilot — no manual record button):
 *   1. Intro: "Let's get to know you" + "Begin"
 *   2. For each prompt:
 *        a. Speak the prompt aloud via SpeechSynthesis (lang=ownerLang).
 *        b. As soon as TTS finishes (or a 2 s fallback), start recording.
 *        c. Auto-stop on ~1.3 s of silence — but only once the answer has
 *           enough voiced audio to clear the server's per-clip floor
 *           (short answers keep the mic open with coaching copy) — or at
 *           the 12 s hard cap.
 *        d. Upload, advance. Retries never re-play the question audio.
 *   3. Finish when the server says `enough`, or all prompts are exhausted.
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { Btn, IconBtn, useLanguages, langByCode } from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";
import { pcm16ToWavBlob } from "../audio/mic";
import { VadRecorder, FRAME_DUR_SEC } from "../audio/recorder";

const CLIP_MAX_SEC = 12;         // hard cap per answer (room for a coached second sentence)
const TARGET_VOICED_SEC = 20; // total target across all prompts
const SILENCE_HANG_FRAMES = 42;  // ~1.34 s @ 32 ms/frame
const TTS_FALLBACK_MS = 2200;    // if speech-synth doesn't fire `onend`
const MAX_REJECTS_PER_PROMPT = 2; // re-attempt a rejected clip before moving on
// Server floor for one clip's voiced audio (enroll_min_clip_voiced_sec).
// `enroll/start` reports the real value; this is the fallback.
const MIN_CLIP_FALLBACK_SEC = 2.0;
// Keep listening slightly past the server minimum so accepted clips clear
// the bar with margin instead of landing exactly on it.
const VOICED_MARGIN_SEC = 0.3;

type Step =
  | { kind: "landing" }
  | { kind: "wizard" }
  | { kind: "done" };

export function EnrollScreen({
  enrollments, onChange, go,
}: {
  enrollments: api.EnrollmentSummary[];
  onChange: () => void;
  go: Go;
}) {
  const [step, setStep] = useState<Step>(enrollments.length === 0 ? { kind: "wizard" } : { kind: "landing" });
  const [err, setErr] = useState<string | null>(null);

  async function activate(id: number) {
    try { await api.activateEnrollment(id); onChange(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  async function remove(id: number) {
    if (!confirm("Delete this voiceprint?")) return;
    try { await api.deleteEnrollment(id); onChange(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  if (step.kind === "wizard") {
    return <Wizard onCancel={() => setStep({ kind: "landing" })} onDone={() => { onChange(); setStep({ kind: "done" }); }} />;
  }
  if (step.kind === "done") {
    return (
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "80px 28px", textAlign: "center" }}>
        <div style={{
          width: 84, height: 84, borderRadius: "50%", background: "color-mix(in oklch, var(--good) 18%, transparent)",
          margin: "0 auto 22px", display: "grid", placeItems: "center", color: "var(--good)",
        }}>
          <Icon name="checkCircle" size={42} />
        </div>
        <h1 className="display" style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)", margin: 0 }}>You're enrolled</h1>
        <p style={{ color: "var(--ink-3)", marginTop: 10, fontSize: 15 }}>
          Your voiceprint is active. Start a live conversation when you're ready.
        </p>
        <div style={{ marginTop: 28, display: "flex", gap: 12, justifyContent: "center" }}>
          <Btn variant="outline" onClick={() => setStep({ kind: "landing" })}>Manage voiceprints</Btn>
          <Btn onClick={() => go("live")} icon="waves">Go live</Btn>
        </div>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <h1 className="display" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.5 }}>
          Your voiceprints
        </h1>
        <Btn icon="plus" onClick={() => setStep({ kind: "wizard" })}>New enrollment</Btn>
      </div>
      {err && <div style={{
        marginBottom: 16, background: "color-mix(in oklch, var(--bad) 12%, transparent)",
        color: "var(--bad)", padding: "10px 14px", borderRadius: 12, fontSize: 13.5,
      }}>{err}</div>}
      {enrollments.length === 0 ? (
        <div style={{ color: "var(--ink-3)" }}>No voiceprints yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {enrollments.map((e) => (
            <div key={e.id} style={{
              background: "var(--surface)", border: `1px solid ${e.active ? "var(--accent)" : "var(--border)"}`,
              borderRadius: "var(--r-lg)", padding: "16px 18px",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14, display: "grid", placeItems: "center",
                background: e.active ? "var(--accent-soft)" : "var(--surface-2)",
                color: e.active ? "var(--accent-ink)" : "var(--ink-3)", flexShrink: 0,
              }}>
                <Icon name="mic" size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>Voiceprint #{e.id}</span>
                  {e.active && <span style={{
                    fontSize: 11, fontWeight: 700, color: "var(--accent-ink)",
                    background: "var(--accent-soft)", padding: "2px 8px", borderRadius: 999, letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}>Active</span>}
                  {e.state === "draft" && <span style={{
                    fontSize: 11, fontWeight: 700, color: "var(--ink-3)",
                    background: "var(--surface-2)", padding: "2px 8px", borderRadius: 999, letterSpacing: 0.4,
                    textTransform: "uppercase",
                  }}>Draft</span>}
                </div>
                <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-faint)", marginTop: 3 }}>
                  {e.voiced_sec != null ? `${e.voiced_sec.toFixed(1)}s voiced` : "no audio"} · {e.clip_count} clips · {new Date(e.created_at).toLocaleDateString()}
                </div>
              </div>
              {!e.active && e.state === "committed" && (
                <Btn variant="tonal" size="sm" onClick={() => activate(e.id)}>Activate</Btn>
              )}
              <IconBtn name="trash" onClick={() => remove(e.id)} size={36} ic={17} color="var(--bad)" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────

type WizardState =
  | { kind: "loading" }
  | { kind: "intro" }
  | { kind: "speaking"; promptIdx: number; totalVoicedSec: number }
  | { kind: "listening"; promptIdx: number; voicedSec: number; needSec: number; totalVoicedSec: number; voicedDetected: boolean }
  | { kind: "uploading"; promptIdx: number; totalVoicedSec: number }
  | { kind: "finishing" }
  | { kind: "error"; message: string };

export function EnrollmentWizard(props: {
  onCancel: () => void;
  onDone: () => void;
  /**
   * Optional override for the per-session voiced-audio target (seconds).
   * Used by the marketing demo to cap enrollment at ~10s so visitors can
   * try the live flow quickly. Falls back to whatever the server returns
   * from ``enroll/start`` (or {@link TARGET_VOICED_SEC}) when omitted.
   */
  targetSecOverride?: number;
  /**
   * Optional pinned enrollment language. When set, the wizard skips its
   * own language picker entirely — used by the demo where the visitor has
   * already chosen a speaker language in a previous step.
   */
  lockedLang?: string;
}) {
  return <Wizard {...props} />;
}

function Wizard({ onCancel, onDone, targetSecOverride, lockedLang }: {
  onCancel: () => void; onDone: () => void;
  targetSecOverride?: number; lockedLang?: string;
}) {
  const user = authStore((s) => s.user);
  const [enrollLang, setEnrollLang] = useState(lockedLang ?? user?.owner_lang ?? "en-US");
  const langs = useLanguages();
  const [prompts, setPrompts] = useState<api.EnrollPrompt[]>([]);
  const initialTarget = targetSecOverride ?? TARGET_VOICED_SEC;
  const [targetSec, setTargetSec] = useState(initialTarget);
  const [state, setState] = useState<WizardState>({ kind: "loading" });
  const recorderRef = useRef<VadRecorder | null>(null);
  const cancelledRef = useRef(false);

  // Hold latest values in refs so the async chain doesn't capture stale state.
  const promptsRef = useRef<api.EnrollPrompt[]>([]);
  const enrollmentIdRef = useRef<number | null>(null);
  const totalVoicedRef = useRef(0);
  // Track rejected-clip retries and whether the server ever accepted a clip,
  // so we never silently march to enroll/finish (which 400s) on bad audio.
  const rejectStreakRef = useRef(0);
  const acceptedClipsRef = useRef(0);
  const [retryHint, setRetryHint] = useState<string | null>(null);
  // Server's per-clip voiced-audio floor (from enroll/start) + current prompt,
  // mirrored into refs for the visibility-recovery handler.
  const minClipSecRef = useRef(MIN_CLIP_FALLBACK_SEC);
  const promptIdxRef = useRef(0);
  const stateKindRef = useRef<WizardState["kind"]>("loading");
  stateKindRef.current = state.kind;
  // Bumped by manual navigation (skip). In-flight runPrompt chains capture
  // the value at entry and go quiet if it changed across an await — same
  // pattern as the LiveEngine mic epoch.
  const runEpochRef = useRef(0);
  // Currently playing prebaked prompt audio, so skip can silence it.
  const promptAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // React 18 StrictMode double-invokes effects in dev: mount → cleanup →
    // mount. The cleanup below flips ``cancelledRef`` to true, and without
    // resetting it on (re-)mount every async action — including the first
    // ``runPrompt(0)`` after the user clicks "Begin" — bails out
    // immediately as if the wizard were cancelled.
    cancelledRef.current = false;
    (async () => {
      try {
        const [start, pr] = await Promise.all([
          api.enrollStart(),
          api.getEnrollPrompts(enrollLang, 8),
        ]);
        enrollmentIdRef.current = start.enrollment_id;
        minClipSecRef.current = start.min_clip_voiced_sec || MIN_CLIP_FALLBACK_SEC;
        // If the caller pinned a target (demo flow), honour it; otherwise
        // accept whatever the server suggests.
        setTargetSec(targetSecOverride ?? (start.target_voiced_sec || TARGET_VOICED_SEC));
        promptsRef.current = pr.prompts.slice(0, 8);
        setPrompts(promptsRef.current);
        setState({ kind: "intro" });
      } catch (e) {
        setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      cancelledRef.current = true;
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      recorderRef.current?.stop().catch(() => { /* ignore */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the screen awake for the whole wizard — mobile screens sleeping
  // mid-answer suspend the AudioContext and silently kill the recording.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let released = false;
    const acquire = () => {
      if (released) return;
      navigator.wakeLock?.request("screen")
        .then((l) => { if (!released) lock = l; else l.release(); })
        .catch(() => {});
    };
    acquire();
    const onVisChange = () => {
      if (document.visibilityState !== "visible") return;
      acquire(); // the lock auto-releases whenever the tab is hidden
      // If we were mid-answer while hidden, the AudioContext is usually no
      // longer running and the recorder is dead air. Throw it away and
      // re-listen for the current prompt (without re-asking the question).
      // The abandoned recorder's run() promise never settles, so the old
      // runPrompt chain simply goes quiet instead of double-advancing.
      const rec = recorderRef.current;
      if (stateKindRef.current === "listening" && rec && rec.ctxState() !== "running") {
        recorderRef.current = null;
        rec.stop().catch(() => { /* ignore */ });
        void runPrompt(promptIdxRef.current, { skipSpeak: true });
      }
    };
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisChange);
      lock?.release().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCancel() {
    cancelledRef.current = true;
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    recorderRef.current?.stop().catch(() => { /* ignore */ });
    onCancel();
  }

  async function runPrompt(idx: number, opts: { skipSpeak?: boolean } = {}) {
    if (cancelledRef.current) return;
    promptIdxRef.current = idx;
    const epoch = runEpochRef.current;
    const stale = () => cancelledRef.current || runEpochRef.current !== epoch;
    const prompt = promptsRef.current[idx];
    if (!prompt) { void finish(); return; }

    // 1) Speak the prompt (prebaked audio if the server has it, else TTS).
    // Skipped on retries — the question is already on screen, and re-playing
    // it was the single slowest, clunkiest beat of the reject loop.
    if (!opts.skipSpeak) {
      setState({ kind: "speaking", promptIdx: idx, totalVoicedSec: totalVoicedRef.current });
      if (prompt.audioUrl) {
        await playPromptAudio(prompt.audioUrl, promptAudioRef);
      } else {
        await speakPrompt(prompt.text, enrollLang);
      }
      if (stale()) return;
    }

    // 2) Listen. The silence auto-stop is not allowed until the answer has
    // more voiced audio than the server's per-clip floor — a one-word answer
    // keeps the mic open (with coaching copy) instead of being recorded,
    // uploaded, and bounced.
    const needSec = minClipSecRef.current + VOICED_MARGIN_SEC;
    setState({
      kind: "listening", promptIdx: idx, voicedSec: 0, needSec,
      totalVoicedSec: totalVoicedRef.current, voicedDetected: false,
    });
    let samples: Int16Array;
    let gotVoicedSec = 0;
    try {
      const rec = new VadRecorder({
        maxSec: CLIP_MAX_SEC,
        silenceHangFrames: SILENCE_HANG_FRAMES,
        minVoicedFrames: Math.ceil(needSec / FRAME_DUR_SEC),
        onTick: (voicedSec, voicedDetected) => {
          setState((s) => (s.kind === "listening"
            ? { ...s, voicedSec, voicedDetected }
            : s));
        },
      });
      recorderRef.current = rec;
      ({ samples, voicedSec: gotVoicedSec } = await rec.run());
      recorderRef.current = null;
    } catch (e) {
      recorderRef.current = null;
      if (stale()) return;
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      return;
    }
    if (stale()) return;

    // 2b) Local pre-check: a clip below the server floor (hit the hard cap
    // while mostly silent) is a guaranteed rejection — don't burn the upload
    // round-trip. And rather than repeating the same question (awkward),
    // move on: the prompt list is an authored scene walk, so the next
    // prompt reads as a natural follow-up, not a reset.
    if (gotVoicedSec < minClipSecRef.current) {
      rejectStreakRef.current = 0;
      setRetryHint(null);
      const next = idx + 1;
      if (next >= promptsRef.current.length) { await finish(); return; }
      void runPrompt(next);
      return;
    }

    // 3) Upload.
    setState({ kind: "uploading", promptIdx: idx, totalVoicedSec: totalVoicedRef.current });
    try {
      const wav = pcm16ToWavBlob(samples);
      const resp = await api.enrollClip(enrollmentIdRef.current!, prompt.id, wav);
      if (stale()) return;

      if (!resp.accepted) {
        // Too short → chain to the scene's next follow-up question (same as
        // the local pre-check; repeating the question feels broken). Quality
        // problems (noisy / clipping) → the environment is the issue, so
        // re-listen to the SAME prompt with an actionable hint; after a few
        // strikes move on so one bad prompt can't trap the user.
        if ((resp.reason ?? "").startsWith("too_short")) {
          rejectStreakRef.current = 0;
          setRetryHint(null);
          const next = idx + 1;
          if (next >= promptsRef.current.length) { await finish(); return; }
          void runPrompt(next);
          return;
        }
        rejectStreakRef.current += 1;
        if (rejectStreakRef.current <= MAX_REJECTS_PER_PROMPT) {
          setRetryHint(friendlyRejectReason(resp.reason));
          void runPrompt(idx, { skipSpeak: true });
          return;
        }
        rejectStreakRef.current = 0;
        setRetryHint(null);
        const nextAfterReject = idx + 1;
        if (nextAfterReject >= promptsRef.current.length) { await finish(); return; }
        void runPrompt(nextAfterReject);
        return;
      }

      // Accepted — clear any retry hint and bank the progress.
      rejectStreakRef.current = 0;
      setRetryHint(null);
      acceptedClipsRef.current += 1;
      totalVoicedRef.current = resp.cumulative_voiced_sec;

      if (resp.enough) { await finish(); return; }
      // Honour any client-side target override (e.g. demo flow caps at
      // ~10s). The server keeps a higher floor for quality, but for demo
      // accounts the relaxed `enroll_demo_hard_floor_sec` lets `enroll/finish`
      // succeed early.
      if (resp.cumulative_voiced_sec >= targetSec) { await finish(); return; }
      const nextIdx = idx + 1;
      if (nextIdx >= promptsRef.current.length) { await finish(); return; }
      // Continue.
      void runPrompt(nextIdx);
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  /** Manual "I'm done": finalize the current answer immediately instead of
   *  waiting out the 1.3 s silence hang. Only offered once the answer has
   *  enough voiced audio to pass, so it can't create a doomed clip. */
  function doneNow() {
    recorderRef.current?.finishNow();
  }

  /** Manual "ask me something else": abandon the current prompt (mid-question
   *  or mid-answer) and move to the next one. The stranded runPrompt chain is
   *  invalidated via the epoch, and the abandoned recorder's run() promise
   *  never settles, so nothing double-advances. */
  function skipPrompt() {
    runEpochRef.current += 1;
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    const a = promptAudioRef.current;
    if (a) {
      promptAudioRef.current = null;
      try { a.pause(); a.src = ""; } catch { /* ignore */ }
    }
    const rec = recorderRef.current;
    recorderRef.current = null;
    rec?.stop().catch(() => { /* ignore */ });
    rejectStreakRef.current = 0;
    setRetryHint(null);
    const next = promptIdxRef.current + 1;
    if (next >= promptsRef.current.length) { void finish(); return; }
    void runPrompt(next);
  }

  async function finish() {
    if (acceptedClipsRef.current === 0) {
      // Every clip was rejected (silence / noise / clipping). Surface a clear
      // recovery message instead of letting enroll/finish 400 with a terse
      // "No clips uploaded for this enrollment".
      setState({
        kind: "error",
        message: "We couldn't capture a clear recording. Find a quieter spot, move closer to the mic, and try again.",
      });
      return;
    }
    setState({ kind: "finishing" });
    try {
      await api.enrollFinish(enrollmentIdRef.current!);
      onDone();
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  // ── render ────────────────────────────────────────────────────────────

  if (state.kind === "loading") return <Centered>Preparing…</Centered>;
  if (state.kind === "finishing") return <Centered>Finalizing your voiceprint…</Centered>;
  if (state.kind === "error") {
    return (
      <Centered>
        <div style={{ color: "var(--bad)", marginBottom: 16 }}>{state.message}</div>
        <Btn variant="outline" onClick={handleCancel}>Back</Btn>
      </Centered>
    );
  }
  if (state.kind === "intro") {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "56px 28px 60px", textAlign: "center" }}>
        <div style={{
          width: 96, height: 96, borderRadius: "50%", margin: "0 auto 24px",
          background: "var(--accent-soft)", color: "var(--accent-ink)",
          display: "grid", placeItems: "center",
        }}>
          <Icon name="mic" size={44} />
        </div>
        <h1 className="display" style={{
          fontSize: 30, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.5,
        }}>
          Let's get to know you
        </h1>
        <p style={{
          color: "var(--ink-2)", marginTop: 14, fontSize: 16, lineHeight: 1.55, maxWidth: 460,
          marginInline: "auto",
        }}>
          We'll ask a few short questions. Answer each in a sentence or two —
          like you're chatting with a friend. About {Math.round(targetSec)} seconds
          of talking and you're done.
        </p>
        <ul style={{
          listStyle: "none", padding: 0, margin: "28px auto 32px", maxWidth: 380,
          textAlign: "left", color: "var(--ink-2)", fontSize: 14.5, lineHeight: 1.7,
        }}>
          <li style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 3 }}><Icon name="volume" size={16} /></span>
            <span>Your phone will ask a question.</span>
          </li>
          <li style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 6 }}>
            <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 3 }}><Icon name="mic" size={16} /></span>
            <span>Answer in a sentence or two. Recording starts and stops on its own.</span>
          </li>
          <li style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 6 }}>
            <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 3 }}><Icon name="checkCircle" size={16} /></span>
            <span>That's it — no buttons to press.</span>
          </li>
        </ul>
        <div style={{ maxWidth: 380, margin: "0 auto 24px", textAlign: "left" }}>
          {lockedLang ? (
            <div style={{
              fontSize: 13, color: "var(--ink-3)", textAlign: "center",
              padding: "10px 14px", background: "var(--surface-2)",
              border: "1px solid var(--border)", borderRadius: "var(--r-md)",
            }}>
              Enrolling in <strong style={{ color: "var(--ink)" }}>{langByCode(enrollLang, langs)?.name ?? enrollLang}</strong>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", marginBottom: 7 }}>Enrollment language</div>
              <select value={enrollLang} onChange={(e) => {
                const code = e.target.value;
                setEnrollLang(code);
                // Re-fetch prompts for the new language
                api.getEnrollPrompts(code, 8).then((pr) => {
                  promptsRef.current = pr.prompts.slice(0, 8);
                  setPrompts(promptsRef.current);
                }).catch(() => {});
                // Also update user's owner_lang
                api.patchMe({ owner_lang: code }).then((me) => authStore.getState().setUser(me)).catch(() => {});
              }} style={{
                width: "100%", height: 50, padding: "0 14px", fontSize: 15.5, color: "var(--ink)",
                background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: "var(--r-md)",
                fontFamily: "var(--font-ui)", appearance: "none", cursor: "pointer",
              }}>
                {langs.map((l) => <option key={l.code} value={l.code}>{l.name} — {l.native}</option>)}
              </select>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
          <Btn size="lg" icon="mic" onClick={() => void runPrompt(0)}>Begin</Btn>
        </div>
      </div>
    );
  }

  // active states: speaking / listening / uploading
  const idx = state.promptIdx;
  const prompt = promptsRef.current[idx];
  const voicedSec = state.kind === "listening" ? state.voicedSec : 0;
  const total = state.totalVoicedSec + (state.kind === "listening" ? voicedSec : 0);
  const progress = Math.min(1, total / Math.max(1, targetSec));
  const recording = state.kind === "listening";
  const speaking = state.kind === "speaking";
  const uploading = state.kind === "uploading";

  const needSec = state.kind === "listening" ? state.needSec : minClipSecRef.current + VOICED_MARGIN_SEC;
  const enough = voicedSec >= needSec;
  let statusText = "";
  if (speaking) statusText = "Listen…";
  else if (uploading) statusText = "Got it.";
  else if (recording && !state.voicedDetected) statusText = "Now answer out loud…";
  else if (recording && !enough) statusText = "Nice — keep going, tell me a bit more…";
  else if (recording) statusText = "Great — pause whenever you're done";

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 28px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <button onClick={handleCancel} style={{
          background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-3)",
          display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-ui)",
          fontSize: 14, fontWeight: 600, padding: 4,
        }}><Icon name="arrowLeft" size={16} /> Cancel</button>
        <div className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {idx + 1} / {prompts.length}
        </div>
      </div>
      <div className="mono" style={{
        fontSize: 11, letterSpacing: 1.6, color: speaking ? "var(--accent)" : "var(--ink-3)",
        textTransform: "uppercase", marginBottom: 12,
      }}>
        {speaking ? "Reading aloud" : recording ? "Your answer" : "One moment"}
      </div>
      <div className="display" style={{
        fontSize: 24, fontWeight: 600, color: "var(--ink)", lineHeight: 1.4, marginBottom: 28, minHeight: 100,
      }}>
        {prompt?.text ?? ""}
      </div>
      <div style={{ display: "flex", justifyContent: "center", margin: "12px 0 8px" }}>
        <RecordRing progress={progress} recording={recording}>
          <div style={{
            width: 110, height: 110, borderRadius: "50%",
            background: recording ? "var(--bad)" : speaking ? "var(--accent)" : "var(--surface-2)",
            color: recording || speaking ? "var(--on-accent)" : "var(--ink-3)",
            display: "grid", placeItems: "center", boxShadow: recording || speaking ? "var(--shadow-md)" : "none",
            animation: recording || speaking ? "hc-breathe 2s ease-in-out infinite" : undefined,
          }}>
            <Icon name={speaking ? "volume" : recording ? "mic" : "checkCircle"} size={42}
              color={recording || speaking ? "var(--on-accent)" : "var(--ink-3)"} />
          </div>
        </RecordRing>
      </div>
      <div style={{ textAlign: "center", color: "var(--ink-3)", fontSize: 14, marginTop: 8, minHeight: 22 }}>
        {statusText}
      </div>
      {/* Per-answer meter: fills as *voiced* audio accumulates toward the
          server's per-clip floor, turning green once this answer will pass. */}
      {recording && state.voicedDetected && (
        <div style={{
          width: 160, height: 4, borderRadius: 2, background: "var(--surface-3)",
          margin: "10px auto 0", overflow: "hidden",
        }}>
          <div style={{
            height: "100%", width: `${Math.min(100, (voicedSec / needSec) * 100)}%`,
            background: enough ? "var(--good)" : "var(--accent)", transition: "width 0.2s",
          }} />
        </div>
      )}
      {retryHint && (
        <div style={{ textAlign: "center", color: "var(--bad)", fontSize: 13.5, marginTop: 4 }}>
          {retryHint}
        </div>
      )}
      {(state.kind === "listening" || state.kind === "speaking") && (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 18 }}>
          {recording && enough && (
            <Btn size="sm" icon="check" onClick={doneNow}>I'm done</Btn>
          )}
          <Btn size="sm" variant="outline" onClick={skipPrompt}>Ask me something else</Btn>
        </div>
      )}
      <div style={{ marginTop: 22, height: 6, borderRadius: 3, background: "var(--surface-3)", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${Math.min(100, progress * 100)}%`,
          background: "var(--accent)", transition: "width 0.2s",
        }} />
      </div>
      <div className="mono" style={{
        textAlign: "center", marginTop: 8, fontSize: 11.5, color: "var(--ink-faint)",
      }}>
        {total.toFixed(1)}s / {Math.round(targetSec)}s
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
      <div>{children}</div>
    </div>
  );
}

/**
 * Map the server's terse clip-rejection `reason` (e.g. "too_short: …",
 * "clipping: …", "noisy: …") into a friendly, actionable retry hint.
 */
function friendlyRejectReason(reason: string | null | undefined): string {
  const r = reason ?? "";
  if (r.startsWith("too_short")) return "Didn't quite catch that — let's try the same one again.";
  if (r.startsWith("clipping")) return "That came through a bit loud — ease back from the mic and try again.";
  if (r.startsWith("noisy")) return "Too much background noise — find a quieter spot and try again.";
  return "Let's try that one again.";
}

function RecordRing({ progress, recording, children }: { progress: number; recording: boolean; children: React.ReactNode }) {
  const R = 80;
  const C = 2 * Math.PI * R;
  const off = C * (1 - Math.min(1, Math.max(0, progress)));
  return (
    <div style={{ position: "relative", width: 168, height: 168 }}>
      <svg width={168} height={168} viewBox="0 0 168 168">
        <circle cx={84} cy={84} r={R} fill="none" stroke="var(--surface-3)" strokeWidth={4} />
        <circle cx={84} cy={84} r={R} fill="none" stroke={recording ? "var(--bad)" : "var(--accent)"}
          strokeWidth={4} strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off}
          transform="rotate(-90 84 84)" style={{ transition: "stroke-dashoffset 0.2s" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>{children}</div>
    </div>
  );
}

// ─── TTS helper ───────────────────────────────────────────────────────────

/** Play a pre-baked prompt-audio WAV from the server. Auth-aware. The
 *  playing element is parked in `holdRef` so skip can silence it early. */
async function playPromptAudio(
  path: string,
  holdRef?: { current: HTMLAudioElement | null },
): Promise<void> {
  let blobUrl: string | null = null;
  try {
    blobUrl = await api.fetchEnrollAudioBlobUrl(path);
  } catch {
    // Server audio unavailable — caller should fall back to SpeechSynthesis,
    // but we resolve here so the wizard keeps moving.
    return;
  }
  return new Promise<void>((resolve) => {
    const audio = new Audio(blobUrl!);
    if (holdRef) holdRef.current = audio;
    let done = false;
    const settle = () => {
      if (done) return;
      done = true;
      if (holdRef && holdRef.current === audio) holdRef.current = null;
      try { if (blobUrl) URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      resolve();
    };
    audio.onended = settle;
    audio.onerror = settle;
    // Safety cap.
    setTimeout(settle, 12_000);
    audio.play().catch(() => settle());
  });
}

function pickVoiceFor(lang: string): SpeechSynthesisVoice | null {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const exact = voices.find((v) => v.lang.toLowerCase() === lang.toLowerCase());
  if (exact) return exact;
  const head = lang.split("-")[0]!.toLowerCase();
  const same = voices.find((v) => v.lang.toLowerCase().startsWith(head + "-"));
  return same ?? null;
}

function speakPrompt(text: string, lang: string): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      // No TTS: insert a short pause so the user has time to read.
      setTimeout(resolve, 1200);
      return;
    }
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang;
    const v = pickVoiceFor(lang);
    if (v) utter.voice = v;
    utter.rate = 0.95;
    utter.pitch = 1;
    let done = false;
    const settle = () => { if (done) return; done = true; resolve(); };
    utter.onend = settle;
    utter.onerror = settle;
    // Some browsers (notably Chrome) lose `onend` if a tab is backgrounded; cap with a fallback.
    const fallbackMs = Math.max(TTS_FALLBACK_MS, Math.min(8000, text.length * 80));
    setTimeout(settle, fallbackMs);
    try { window.speechSynthesis.speak(utter); }
    catch { settle(); }
  });
}
